import { Extension } from "@tiptap/core";

/**
 * Paragraph-level formatting for the day-note editor: line height, space
 * before/after a paragraph, and block indent. Google-Docs-like: the toolbar
 * applies these to the whole paragraph under the cursor/selection, not to an
 * inline run (line-height on a <span> only affects part of a wrapped line).
 *
 * The four values are stored as attributes on the paragraph node and emitted
 * as one combined inline `style` (ADR-0011). ProseMirror merges each
 * attribute's renderHTML output, so only the `style` attribute renders and the
 * rest return null — a later-wins overwrite of `style` is impossible.
 */

export interface ParagraphFormatOptions {
  /** Node types the format attributes apply to. */
  types: string[];
  /** Block-indent step and ceiling, in px. */
  indentStep: number;
  indentMax: number;
  /** Space-before/after toggle value, in px. */
  spacing: number;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    paragraphFormat: {
      setLineHeight: (value: string) => ReturnType;
      unsetLineHeight: () => ReturnType;
      toggleSpacingBefore: () => ReturnType;
      toggleSpacingAfter: () => ReturnType;
      indent: () => ReturnType;
      outdent: () => ReturnType;
      clearParagraphFormat: () => ReturnType;
    };
  }
}

interface ParagraphFormatAttrs {
  indent: number;
  lineHeight: string | null;
  spacingBefore: number;
  spacingAfter: number;
}

/** Read a px value from an element style ("" / "auto" -> 0). */
function pxOf(value: string | null | undefined): number {
  const n = value ? parseInt(value, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function styleOf(attrs: ParagraphFormatAttrs): string | null {
  const parts: string[] = [];
  if (attrs.indent > 0) parts.push(`margin-left:${attrs.indent}px`);
  if (attrs.lineHeight) parts.push(`line-height:${attrs.lineHeight}`);
  if (attrs.spacingBefore > 0) parts.push(`margin-top:${attrs.spacingBefore}px`);
  if (attrs.spacingAfter > 0) parts.push(`margin-bottom:${attrs.spacingAfter}px`);
  return parts.length > 0 ? parts.join("; ") : null;
}

export const ParagraphFormat = Extension.create<ParagraphFormatOptions>({
  name: "paragraphFormat",

  addOptions() {
    return {
      types: ["paragraph"],
      indentStep: 24,
      indentMax: 120,
      spacing: 12,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => pxOf(element.style.marginLeft),
            renderHTML: () => null,
          },
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight || null,
            renderHTML: () => null,
          },
          spacingBefore: {
            default: 0,
            parseHTML: (element) => pxOf(element.style.marginTop),
            renderHTML: () => null,
          },
          spacingAfter: {
            default: 0,
            parseHTML: (element) => pxOf(element.style.marginBottom),
            renderHTML: () => null,
          },
          // Single style emitter: renderHTML receives the full attribute set
          // and emits one combined style string for the paragraph.
          style: {
            default: null,
            parseHTML: (element) => element.getAttribute("style") || null,
            renderHTML: (attrs) => {
              const s = styleOf(attrs as unknown as ParagraphFormatAttrs);
              return s ? { style: s } : null;
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (value: string) =>
        ({ commands }) =>
          commands.updateAttributes("paragraph", { lineHeight: value || null }),
      unsetLineHeight:
        () =>
        ({ commands }) =>
          commands.updateAttributes("paragraph", { lineHeight: null }),
      toggleSpacingBefore:
        () =>
        ({ editor, commands }) => {
          const current = (editor.getAttributes("paragraph").spacingBefore as number | undefined) ?? 0;
          return commands.updateAttributes("paragraph", { spacingBefore: current > 0 ? 0 : this.options.spacing });
        },
      toggleSpacingAfter:
        () =>
        ({ editor, commands }) => {
          const current = (editor.getAttributes("paragraph").spacingAfter as number | undefined) ?? 0;
          return commands.updateAttributes("paragraph", { spacingAfter: current > 0 ? 0 : this.options.spacing });
        },
      indent:
        () =>
        ({ editor, commands }) => {
          const current = (editor.getAttributes("paragraph").indent as number | undefined) ?? 0;
          return commands.updateAttributes("paragraph", { indent: Math.min(current + this.options.indentStep, this.options.indentMax) });
        },
      outdent:
        () =>
        ({ editor, commands }) => {
          const current = (editor.getAttributes("paragraph").indent as number | undefined) ?? 0;
          return commands.updateAttributes("paragraph", { indent: Math.max(current - this.options.indentStep, 0) });
        },
      clearParagraphFormat:
        () =>
        ({ commands }) =>
          commands.updateAttributes("paragraph", { indent: 0, lineHeight: null, spacingBefore: 0, spacingAfter: 0 }),
    };
  },

  addKeyboardShortcuts() {
    return {
      "Mod-]": () => this.editor.commands.indent(),
      "Mod-[": () => this.editor.commands.outdent(),
    };
  },
});
